// src/lib/audio.ts
// Audio utilities for Gemini Live API - handles PCM encoding/decoding and playback

/**
 * Captures microphone audio and returns PCM16 chunks as base64
 */
export class AudioRecorder {
  private stream: MediaStream | null = null
  private audioContext: AudioContext | null = null
  private processor: ScriptProcessorNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  onData: ((base64Chunk: string) => void) | null = null

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    })

    this.audioContext = new AudioContext({ sampleRate: 16000 })
    this.source = this.audioContext.createMediaStreamSource(this.stream)

    // Use ScriptProcessorNode for raw PCM access
    // Buffer size 4096 = ~256ms chunks at 16kHz
    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1)

    this.processor.onaudioprocess = (event) => {
      if (!this.onData) return
      const float32Data = event.inputBuffer.getChannelData(0)
      const pcm16 = float32ToInt16(float32Data)
      const base64 = arrayBufferToBase64(pcm16.buffer)
      this.onData(base64)
    }

    this.source.connect(this.processor)
    this.processor.connect(this.audioContext.destination)
  }

  stop(): void {
    this.processor?.disconnect()
    this.source?.disconnect()
    this.stream?.getTracks().forEach(track => track.stop())
    this.audioContext?.close()
    this.stream = null
    this.audioContext = null
    this.processor = null
    this.source = null
  }

  isRecording(): boolean {
    return this.stream !== null
  }
}

/**
 * Plays PCM16 audio chunks received from Gemini Live
 */
export class AudioPlayer {
  private audioContext: AudioContext | null = null
  private nextStartTime = 0
  private playing = false

  async init(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext({ sampleRate: 24000 })
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }
  }

  async playChunk(base64PcmData: string): Promise<void> {
    await this.init()
    const ctx = this.audioContext!

    const pcm16 = base64ToInt16Array(base64PcmData)
    const float32 = int16ToFloat32(pcm16)

    const audioBuffer = ctx.createBuffer(1, float32.length, 24000)
    audioBuffer.getChannelData(0).set(float32)

    const source = ctx.createBufferSource()
    source.buffer = audioBuffer
    source.connect(ctx.destination)

    // Schedule playback to avoid gaps
    const currentTime = ctx.currentTime
    if (this.nextStartTime < currentTime) {
      this.nextStartTime = currentTime
    }

    source.start(this.nextStartTime)
    this.nextStartTime += audioBuffer.duration
    this.playing = true

    source.onended = () => {
      if (this.nextStartTime <= ctx.currentTime + 0.05) {
        this.playing = false
      }
    }
  }

  stop(): void {
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
    this.nextStartTime = 0
    this.playing = false
  }

  isPlaying(): boolean {
    return this.playing
  }

  reset(): void {
    this.nextStartTime = 0
    this.playing = false
  }
}

// --- Helper functions ---

function float32ToInt16(float32Array: Float32Array): Int16Array {
  const int16Array = new Int16Array(float32Array.length)
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]))
    int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
  }
  return int16Array
}

function int16ToFloat32(int16Array: Int16Array): Float32Array {
  const float32Array = new Float32Array(int16Array.length)
  for (let i = 0; i < int16Array.length; i++) {
    float32Array[i] = int16Array[i] / 0x7FFF
  }
  return float32Array
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function base64ToInt16Array(base64: string): Int16Array {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return new Int16Array(bytes.buffer)
}
